import { type ReactNode, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { firstValidationMessage } from '@/lib/api/client';
import { useToast } from '@/components/ui/toast';
import { type ComboItem } from './EntityCombobox';
import { useReferenceData } from './useReferenceData';
import {
  COMPANIES_QUERY_KEY,
  useCreateCompany,
  useCreateOriginator,
  useCreatePerson,
  useUpdateCompany,
  useUpdateOriginator,
  useUpdatePerson,
} from './useEntities';

const INPUT =
  'w-full rounded border border-vega-line bg-vega-surface px-2.5 py-2 text-[13px] text-vega-ink outline-none';
const LABEL = 'mb-1 block text-[11px] font-medium uppercase tracking-wide text-vega-muted';

/**
 * Mini-formulários de criar-inline de Empresa / Originadora / Pessoa, compartilhados pelo +Novo Deal
 * (NewDealModal) e pela ficha de edição (DealEditForm). Reusam o EntityCombobox e os hooks de
 * entidade; o overlay (InlineShell) usa z-[90]/z-[100], acima do modal de novo deal (z-[80]) e do
 * drawer da ficha (z-[60]). Extraídos do NewDealModal no lote 1 (follow-up) para evitar duplicação.
 */

/** Casca do mini-formulário de criar-inline (overlay acima do modal/drawer que o abriu). */
export function InlineShell({
  title,
  canSave,
  saving,
  onSave,
  onCancel,
  children,
  saveLabel = 'Criar',
  savingLabel = 'Criando…',
}: {
  title: string;
  canSave: boolean;
  saving: boolean;
  onSave: () => void;
  onCancel: () => void;
  children: ReactNode;
  saveLabel?: string;
  savingLabel?: string;
}) {
  return (
    <>
      <button type="button" aria-label="Cancelar" onClick={onCancel} className="fixed inset-0 z-[90] cursor-default bg-[rgba(20,18,12,0.45)]" />
      <div role="dialog" aria-label={title} className="fixed left-1/2 top-1/2 z-[100] w-[min(420px,92vw)] -translate-x-1/2 -translate-y-1/2 rounded-lg bg-vega-surface p-5 shadow-vega-lg">
        <h3 className="mb-3 text-[14px] font-semibold text-vega-ink">{title}</h3>
        <div className="space-y-3">{children}</div>
        <div className="mt-4 flex justify-end gap-2">
          <button type="button" onClick={onCancel} className="rounded border border-vega-line px-3 py-1.5 text-[12.5px] text-vega-muted">
            Cancelar
          </button>
          <button
            type="button"
            onClick={onSave}
            disabled={!canSave || saving}
            className="rounded bg-vega-ink px-3 py-1.5 text-[12.5px] font-semibold text-vega-bg disabled:opacity-50"
          >
            {saving ? savingLabel : saveLabel}
          </button>
        </div>
      </div>
    </>
  );
}

export function CompanyInline({
  draft,
  edit,
  onCreated,
  onCancel,
}: {
  draft: string;
  /** Modo edição (Item 6): pré-preenche e chama UpdateCompany em vez de criar. */
  edit?: { id: string; name: string; sectorId: number };
  onCreated: (i: ComboItem) => void;
  onCancel: () => void;
}) {
  const reference = useReferenceData();
  const qc = useQueryClient();
  const toast = useToast();
  const createCompany = useCreateCompany();
  const updateCompany = useUpdateCompany();
  const [name, setName] = useState(edit?.name ?? draft);
  const [sectorId, setSectorId] = useState<number | ''>(edit?.sectorId ?? '');
  const canSave = name.trim().length > 0 && sectorId !== '';
  const saving = createCompany.isPending || updateCompany.isPending;
  const save = () => {
    const onSuccess = (c: { id: string; name: string }) => onCreated({ id: c.id, name: c.name });
    // 400 de dedup (nome+setor já existe): mensagem clara + revalida a lista, pra quem cancelar o
    // mini-form já achar a empresa existente no combobox (a lista já é buscada fresca ao abrir — ver
    // useEntities — mas revalida de novo aqui por segurança).
    const onError = (err: unknown) => {
      toast(firstValidationMessage(err) ?? 'Não foi possível salvar a empresa.', 'danger');
      void qc.invalidateQueries({ queryKey: COMPANIES_QUERY_KEY });
    };
    if (edit) {
      updateCompany.mutate({ id: edit.id, name: name.trim(), sectorId: Number(sectorId) }, { onSuccess, onError });
    } else {
      createCompany.mutate({ name: name.trim(), sectorId: Number(sectorId) }, { onSuccess, onError });
    }
  };
  return (
    <InlineShell
      title={edit ? 'Editar empresa' : 'Nova empresa'}
      canSave={canSave}
      saving={saving}
      onSave={save}
      onCancel={onCancel}
      saveLabel={edit ? 'Salvar' : 'Criar'}
      savingLabel={edit ? 'Salvando…' : 'Criando…'}
    >
      <div>
        <span className={LABEL}>Nome *</span>
        <input value={name} onChange={(e) => setName(e.target.value)} className={INPUT} />
      </div>
      <div>
        <span className={LABEL}>Setor *</span>
        <select value={sectorId} onChange={(e) => setSectorId(e.target.value === '' ? '' : Number(e.target.value))} className={INPUT}>
          <option value="">Selecione…</option>
          {reference.data?.sectors.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </select>
      </div>
    </InlineShell>
  );
}

export function OriginatorInline({
  draft,
  edit,
  onCreated,
  onCancel,
}: {
  draft: string;
  edit?: { id: string; name: string; typeId: number };
  onCreated: (i: ComboItem) => void;
  onCancel: () => void;
}) {
  const reference = useReferenceData();
  const createOriginator = useCreateOriginator();
  const updateOriginator = useUpdateOriginator();
  const [name, setName] = useState(edit?.name ?? draft);
  const [typeId, setTypeId] = useState<number | ''>(edit?.typeId ?? '');
  const canSave = name.trim().length > 0 && typeId !== '';
  const saving = createOriginator.isPending || updateOriginator.isPending;
  const save = () => {
    const onSuccess = (o: { id: string; name: string }) => onCreated({ id: o.id, name: o.name });
    if (edit) {
      updateOriginator.mutate({ id: edit.id, name: name.trim(), typeId: Number(typeId) }, { onSuccess });
    } else {
      createOriginator.mutate({ name: name.trim(), typeId: Number(typeId) }, { onSuccess });
    }
  };
  return (
    <InlineShell
      title={edit ? 'Editar originadora' : 'Nova originadora'}
      canSave={canSave}
      saving={saving}
      onSave={save}
      onCancel={onCancel}
      saveLabel={edit ? 'Salvar' : 'Criar'}
      savingLabel={edit ? 'Salvando…' : 'Criando…'}
    >
      <div>
        <span className={LABEL}>Nome *</span>
        <input value={name} onChange={(e) => setName(e.target.value)} className={INPUT} />
      </div>
      <div>
        <span className={LABEL}>Tipo *</span>
        <select value={typeId} onChange={(e) => setTypeId(e.target.value === '' ? '' : Number(e.target.value))} className={INPUT}>
          <option value="">Selecione…</option>
          {reference.data?.originatorTypes.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name}
            </option>
          ))}
        </select>
      </div>
    </InlineShell>
  );
}

export function PersonInline({
  draft,
  originatorId,
  originatorName,
  edit,
  onCreated,
  onCancel,
}: {
  draft: string;
  /** Originadora selecionada no form — a pessoa nasce/segue vinculada a ela (Item 2, lote 3). */
  originatorId?: string | null;
  originatorName?: string | null;
  /** Modo edição (Item 6): pré-preenche e chama UpdatePerson. */
  edit?: { id: string; name: string; email: string | null };
  onCreated: (i: ComboItem) => void;
  onCancel: () => void;
}) {
  const createPerson = useCreatePerson();
  const updatePerson = useUpdatePerson();
  const [name, setName] = useState(edit?.name ?? draft);
  const [email, setEmail] = useState(edit?.email ?? '');
  const canSave = name.trim().length > 0;
  const saving = createPerson.isPending || updatePerson.isPending;
  const save = () => {
    const onSuccess = (p: { id: string; name: string }) => onCreated({ id: p.id, name: p.name });
    if (edit) {
      updatePerson.mutate(
        { id: edit.id, name: name.trim(), email: email.trim() || null, currentOriginatorId: originatorId ?? null },
        { onSuccess },
      );
    } else {
      createPerson.mutate(
        { name: name.trim(), email: email.trim() || null, currentOriginatorId: originatorId ?? null },
        { onSuccess },
      );
    }
  };
  return (
    <InlineShell
      title={edit ? 'Editar pessoa' : 'Nova pessoa'}
      canSave={canSave}
      saving={saving}
      onSave={save}
      onCancel={onCancel}
      saveLabel={edit ? 'Salvar' : 'Criar'}
      savingLabel={edit ? 'Salvando…' : 'Criando…'}
    >
      <div>
        <span className={LABEL}>Nome *</span>
        <input value={name} onChange={(e) => setName(e.target.value)} className={INPUT} />
      </div>
      <div>
        <span className={LABEL}>E-mail</span>
        <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" className={INPUT} />
      </div>
      {originatorId && (
        <p className="text-[11px] text-vega-muted-2">
          Será vinculada à originadora <strong>{originatorName ?? 'selecionada'}</strong>.
        </p>
      )}
    </InlineShell>
  );
}
